import {
  EliteOnboardingRenewalStatus,
  type EliteOnboardingEligiblePlan,
  type EliteOnboardingStatusResponse,
  type EliteSubscriptionPlanDetails
} from '../../../types/misc.types'

/** Row from `eligiblePlans` that best represents the member's current tier when upgrade offers list the target separately. */
export function getEliteFocusEligiblePlan(
  eliteOnboardingStatus?: EliteOnboardingStatusResponse
): EliteOnboardingEligiblePlan | undefined {
  const plans = eliteOnboardingStatus?.eligiblePlans
  if (!plans?.length) {
    return undefined
  }
  const upgradeTargetId = eliteOnboardingStatus?.subscriptionDetails?.upgradeDetails?.upgradePlanDetails?.basePlanId
  if (upgradeTargetId) {
    const nonUpgrade = plans.find((p) => p?.basePlanId != null && p.basePlanId !== upgradeTargetId)
    if (nonUpgrade) {
      return nonUpgrade
    }
  }
  return plans.find((p) => p?.recommended) ?? plans[0]
}

export function getElitePlanDisplayFeesExGst(plan?: EliteOnboardingEligiblePlan): number {
  return plan?.effectivePlanFees?.amount ?? plan?.basePlanFees?.amount ?? 0
}

export function getEligiblePlanCtaAmount(plan?: EliteOnboardingEligiblePlan): number {
  const hasEffectiveOffer = Boolean(plan?.effectivePlanId && plan?.effectivePlanFees)
  if (hasEffectiveOffer) return plan?.effectivePlanFees?.amount ?? 0
  return plan?.basePlanFees?.amount ?? 0
}

export function getEligiblePlanCtaId(plan?: EliteOnboardingEligiblePlan): string {
  return plan?.effectivePlanId ?? plan?.basePlanId ?? ''
}

export function getEliteSubscriptionPlanFeesExGst(plan?: EliteSubscriptionPlanDetails | null): number {
  return plan?.effectivePlanFees?.amount ?? plan?.basePlanFees?.amount ?? 0
}

/** Ex-GST primary row + optional list strike from `subscriptionPlanDetails` (effective on top when set; else base only). */
export type EliteSubscriptionPlanPriceDisplayExGst = {
  primaryExGst: number
  listStrikeExGst: number
  showStrikeThrough: boolean
}

export function getEliteSubscriptionPlanPriceDisplayExGst(
  subscriptionDetails?: EliteOnboardingStatusResponse['subscriptionDetails'] | null,
  activePlan?: EliteOnboardingEligiblePlan
): EliteSubscriptionPlanPriceDisplayExGst {
  const sp = subscriptionDetails?.subscriptionPlanDetails
  if (sp) {
    const effectiveFeesAmount = sp.effectivePlanFees?.amount
    const hasEffectivePlanFees = effectiveFeesAmount != null
    const basePlanFeesAmount = sp.basePlanFees?.amount ?? 0
    const primaryPlanFees = hasEffectivePlanFees ? effectiveFeesAmount : basePlanFeesAmount
    const listStrikeExGst = sp.actualPlanPrice?.amount ?? 0
    return {
      primaryExGst: primaryPlanFees,
      listStrikeExGst,
      showStrikeThrough: listStrikeExGst > primaryPlanFees
    }
  }
  const primaryExGst =
    getEliteSubscriptionDisplayFeesExGst(subscriptionDetails ?? undefined) ??
    getElitePlanDisplayFeesExGst(activePlan) ??
    0
  const listStrikeExGst = activePlan?.actualPlanPrice?.amount ?? 0
  const showStrikeThrough = listStrikeExGst > primaryExGst
  return { primaryExGst, listStrikeExGst, showStrikeThrough }
}

/** Ex-GST amount shown for the active subscription row (legacy `membershipFees` or nested plan details). */
export function isEliteYearlyPlanTenure({ period, value }: { period?: string; value: number }): boolean {
  return period === 'YEAR' || value >= 12
}

export function getElitePlanStatusLabel(eliteOnboardingStatus?: EliteOnboardingStatusResponse): string {
  console.log(
    eliteOnboardingStatus?.renewal?.status === EliteOnboardingRenewalStatus.RENEWAL_AUTOPAY_PAYMENT_FAILED_RETRYING
  )
  if (eliteOnboardingStatus?.renewal?.status === EliteOnboardingRenewalStatus.RENEWAL_AUTOPAY_PAYMENT_FAILED_RETRYING) {
    return 'Expires Soon'
  }
  return 'Active'
}

export function getEliteCurrentPlanTenure(eliteOnboardingStatus?: EliteOnboardingStatusResponse): {
  period?: string
  value: number
} {
  const subscriptionPlan = eliteOnboardingStatus?.subscriptionDetails?.subscriptionPlanDetails
  const activePlan = getEliteFocusEligiblePlan(eliteOnboardingStatus)
  return {
    period:
      subscriptionPlan?.effectivePlanTenure?.period ??
      subscriptionPlan?.basePlanTenure?.period ??
      activePlan?.effectivePlanTenure?.period ??
      activePlan?.basePlanTenure?.period,
    value: Number(
      subscriptionPlan?.effectivePlanTenure?.value ??
        subscriptionPlan?.basePlanTenure?.value ??
        activePlan?.effectivePlanTenure?.value ??
        activePlan?.basePlanTenure?.value ??
        0
    )
  }
}

export function getElitePlanTenureLabel({ period, value }: { period?: string; value: number }): 'Monthly' | 'Yearly' {
  return isEliteYearlyPlanTenure({ period, value }) ? 'Yearly' : 'Monthly'
}

export function getElitePlanStatusLabelFromOnboarding(eliteOnboardingStatus?: EliteOnboardingStatusResponse): string {
  return getElitePlanStatusLabel(eliteOnboardingStatus)
}

export function getEliteSubscriptionDisplayFeesExGst(
  subscriptionDetails?: EliteOnboardingStatusResponse['subscriptionDetails']
): number | undefined {
  const direct = subscriptionDetails?.membershipFees?.amount
  if (direct != null) {
    return direct
  }
  const nested = subscriptionDetails?.subscriptionPlanDetails
  if (!nested) {
    return undefined
  }
  const fromPlan = getEliteSubscriptionPlanFeesExGst(nested)
  return fromPlan
}
